import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import styles from './LogoutAllDevicesModal.module.css';

export interface LogoutAllDevicesModalProps {
    open: boolean;
    sessionCount: number;
    pending: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

function LogoutIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M15 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 12H9M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" strokeLinecap="round" />
        </svg>
    );
}

/**
 * "Đăng xuất mọi thiết bị" confirm — frame 82, on the Phiên tab.
 *
 * EAA-1820: a real centered `Modal` rather than the kit's `ConfirmButton`
 * (antd `Popconfirm`, an inline popover) — frame 82's dialog carries an icon,
 * a two-tone warning card and its own footer note, which `Popconfirm`'s
 * single-line description prop can't lay out. `ProfilePage.tsx` still runs
 * `endAllSessions` (`DELETE /me/sessions`) then the local `logout()`
 * afterwards, same as before this ticket — this component only owns the
 * confirmation UI, not the mutation.
 */
export function LogoutAllDevicesModal({
    open,
    sessionCount,
    pending,
    onClose,
    onConfirm,
}: LogoutAllDevicesModalProps) {
    const { t } = useTranslation();

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={540}
            closable={false}
            maskClosable={!pending}
            title={
                <span className={styles.head}>
                    <span className={styles.icon} aria-hidden="true">
                        <LogoutIcon />
                    </span>
                    <span className={styles.headText}>
                        <span className={styles.title}>
                            {t('profile.logoutAllModal.title', { count: sessionCount })}
                        </span>
                        <span className={styles.desc}>
                            {t('profile.logoutAllModal.description')}
                        </span>
                    </span>
                </span>
            }
            footer={
                <div className={styles.footer}>
                    <span className={styles.footnote}>
                        {t('profile.logoutAllModal.footerNote')}
                    </span>
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                    <Button onClick={onClose} disabled={pending}>
                        {t('profile.logoutAllModal.cancel')}
                    </Button>
                    <Button danger onClick={onConfirm} disabled={pending}>
                        {pending ? t('profile.loggingOutAll') : t('profile.logoutAll')}
                    </Button>
                </div>
            }
        >
            <div className={styles.body}>
                <div className={styles.warning}>
                    <span className={styles.warningTitle}>
                        {t('profile.logoutAllModal.warningTitle')}
                    </span>
                    <span>{t('profile.logoutAllModal.warningDescription')}</span>
                </div>
                <span className={styles.hint}>
                    {t('profile.logoutAllModal.keepThisDeviceNotePrefix')}
                    <strong>{t('profile.logoutAllModal.keepThisDeviceNoteBold')}</strong>
                    {t('profile.logoutAllModal.keepThisDeviceNoteSuffix')}
                </span>
            </div>
        </Modal>
    );
}

export default LogoutAllDevicesModal;
